import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Accordion, Button, Card, Icon, P, Box, Spinner, H3, T, useToast } from '@veracity/vui';
import { useChecklistDetails, useUpdateChecklist } from '../api/checklists';
import { useNavigate } from 'react-router-dom';
import ChecklistHeader from '../components/ChecklistHeader';
import CheckModal from '../components/CheckModal';
import { useChecklistTemplate } from '../api/templates';
import { ChecklistStatus } from '../enums/ChecklistStatus';
import { CheckStatus } from '../enums/CheckStatus';
import { getInputTypeString } from '../enums/InputTypeMap';

const ChecklistView = () => {
  const { checklist_id } = useParams();
  const { data: checklist, isLoading, error } = useChecklistDetails(checklist_id);
  const { data: template, isLoading: isTemplateLoading } = useChecklistTemplate(
    checklist?.templateId,
  );
  const { mutate: updateChecklist, data: updateResponse, isLoading: isUpdating, error: updateError } = useUpdateChecklist();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedCheckId, setSelectedCheckId] = useState('');
  const [localChecklist, setLocalChecklist] = useState({});
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [isSavingToServer, setIsSavingToServer] = useState(false);
  const [isChecklistClosed, setIsChecklistClosed] = useState(false);
  const [modalInputConfig, setModalInputConfig] = useState({
    type: 'Text',
    options: [],
    label: 'Comment',
    required: false
  });

  const { showToast, showSuccess } = useToast();

  useEffect(() => {
    const savedChecklist = localStorage.getItem(`checklist_${checklist_id}`);
    if (savedChecklist) {
      setLocalChecklist(JSON.parse(savedChecklist));
    } else if (checklist) {
      setLocalChecklist(JSON.parse(JSON.stringify(checklist)));
    }
  }, [checklist, checklist_id]);


  useEffect(() => {
    if (updateError) {
      setFeedbackMessage({ type: 'error', text: 'Failed to save checklist' });
      showToast({
        title: "Update Failed",
        text: updateError.message,
        duration: "slow",
        icon: "falExclamationTriangle"
      })
    } 
  }, [updateError, showToast]);

  useEffect(() => {

    // setIsChecklistClosed(localChecklist.status === 0);
    setIsChecklistClosed(localChecklist.status === ChecklistStatus.CLOSED);
  }, [localChecklist.status]);

  if (isLoading || isTemplateLoading) return (
      <Box alignItems="center" justifyContent="center" minH={700}>
        <Spinner />
      </Box>
    );
  
    if (error) return (
      <Box alignItems="center" elevation={2} flexDirection="column" justifyContent="center" maxW={580} minW={580} p={3}>
        <Box><Icon color="energyRed.45" name="falExclamationTriangle" size="xxl" /></Box>
        <H3 my={2}>Something went wrong</H3>
        <T>The operation could not be completed. Try again later.</T>
        <Button mt={3} onClick={() => window.location.reload()}>Try again</Button>
      </Box>
    );

  if (!checklist) return <div>Checklist not found</div>;

  if (!template && !isTemplateLoading) return <div>Template not found</div>;

  

  const findCheckTemplateById = (templateData, checkTag) => {
    if (!templateData || !templateData.sections) return null;
    for (const section of templateData.sections) {
      const check = section.checks.find(c => c.tag === checkTag);
      if (check) return check;
    }
    return null;
  };

  const handleOpenModal = (checkId, action) => {
    if (isChecklistClosed) {
      showToast({
        title: "Checklist Closed",
        text: "This checklist is closed and cannot be modified",
        duration: "slow",
        icon: "falInfoCircle"
      });
      return;
    }

    const check = findCheckById(localChecklist, checkId);
    if (!check) return;

    const checkTemplate = findCheckTemplateById(template, check.tag);
    if (!checkTemplate) return;

    const dialogConfig = action === 'approve' ? checkTemplate.approve : checkTemplate.discard;

    if (dialogConfig) {
      const inputType = getInputTypeString(dialogConfig.type);

      if (inputType === 'Hidden') {
        handleConfirm({
          checkId: checkId,
          action: action,
          value: 'hiddenValue'
        });
        return;
      }

      setModalInputConfig({
        type: inputType || 'Text',
        options: dialogConfig.options || [],
        label: dialogConfig.label || 'Comment',
        required: action === 'approve' ? (dialogConfig.required || false) : false
      });
    } else {
      setModalInputConfig({
        type: 'Text',
        options: [],
        label: 'Comment',
        required: false
      })
    }

    setSelectedCheckId(checkId);
    setModalAction(action);
    setIsModalOpen(true);
  }

  const handleCloseModal = () => {
    setSelectedCheckId('');
    setModalAction('');
    setIsModalOpen(false);
   
  }

  const handleConfirm = async (data) => {
    console.log(`Confirming check: ${data.checkId} with action: ${data.action}`);
    const { checkId, action, value, additionalComment } = data;

    const check = findCheckById(localChecklist, checkId);
    if (!check) return;

    const checkTemplate = findCheckTemplateById(template, check.tag);
    if (!checkTemplate) return;

    const inputType = action === 'approve'
      ? getInputTypeString(checkTemplate.approve?.type || 0) 
      : getInputTypeString(checkTemplate.discard?.type || 0);

    const previousState = JSON.parse(JSON.stringify(localChecklist));

    const updatedChecklist = JSON.parse(JSON.stringify(localChecklist));

    const updateCheck = (checks) => {
      return checks.map(check => {
        if (check.id === checkId) {
          const updatedCheck = {
            ...check,
            status: action === 'approve' ? CheckStatus.APPROVED : CheckStatus.DISCARDED,
            comment: additionalComment || null,
            selectedOption: null,
            dateValue: null,
            timeValue: null,
            DateTimeValue: null,
            urlValue: null,
            textAreaValue: null,
            hiddenValue: null,
            passwordValue: null,
            colorValue: null
          };

          console.log("inputType", inputType);

          if (inputType === 'Text') {
            updateCheck.textValue = value;
          } else if (inputType === 'Checkbox') {
            updatedCheck.selectedOption = value;
          } else if (inputType === 'Date') {
            updatedCheck.dateValue = value;
          } else if (inputType === 'Time') {
            updatedCheck.timeValue = value;
          } else if (inputType === 'DateTime') {
            updatedCheck.dateTimeValue = `${value.date}T${value.time}:00Z`;
          } else if (inputType === 'URL') {
            updatedCheck.urlValue = value;
          } else if (inputType === 'TextArea') {
            updatedCheck.textAreaValue = value;
          } else if (inputType === 'Hidden') {
            updatedCheck.hiddenValue = value;
          } else if (inputType === 'Password') {
            updatedCheck.passwordValue = value;
          } else if (inputType === 'Color') {
            updatedCheck.colorValue = value;
          } else {
            updatedCheck.comment = value;
          }

          return updatedCheck;
        }
        return check;
      });
    };

    updatedChecklist.sections.forEach(section => {
      section.checks = updateCheck(section.checks);
      section.status = section.checks.every(check => check.status === CheckStatus.APPROVED) ? "Approved"
        : section.checks.every(check => check.status === CheckStatus.DISCARDED) ? "Discarded"
        : "Pending";
    });

    setLocalChecklist(updatedChecklist);

      // update the overall status


    try {
      localStorage.setItem(`checklist_${checklist_id}`, JSON.stringify(updatedChecklist));
    } catch (error) {
      console.error("Failed to save to local storage", error);
    }

      // return updatedChecklist;
    // });

    handleCloseModal();

    try {  
      const updateData = {
        status: updatedChecklist.status,
        checks: updatedChecklist.sections.flatMap(section =>
          section.checks.map(check => ({
            id: check.id,
            status: check.status,
            comment: check.comment || "",
            selectedOption: Array.isArray(check.selectedOption) ? check.selectedOption : [],

            textValue: check.textValue || null,
            dateValue: check.dateValue || null,
            timeValue: check.timeValue || null,
            dateTimeValue: check.dateTimeValue || null,
            urlValue: check.urlValue || null,
            textAreaValue: check.textAreaValue || null,
            hiddenValue: check.hiddenValue || null,
            passwordValue: check.passwordValue || null,
            colorValue: check.colorValue || null
          }))
        )
      };

      console.log("Sending updateData to backend:", updateData);
      
      updateChecklist(
        {
          id: checklist_id,
          data: updateData
        },
        {
          onSuccess: (responseData) => {
            console.log("Response data from update:", responseData);

            showToast({
              title: "Check updated",
              text: "Check status updated successfully",
              duration: "slow",
              icon: "falCheckCircle"
            });

            if (responseData?.completedSectionTag) {
              console.log("showing section completed toast", responseData.completedSectionTag);
              showToast({
                title: "Section Completed",
                text: `Section ${responseData.completedSectionTag} completed successfully`,
                duration: "slow",
                icon: "falCheckCircle"
              });
            }
          },
          onError: (error) => {
            console.error("Error updating check:", error);
            showToast({
              title: "Update failed",
              text: "Failed to update check status. The event could not be sent to the server.",
              duration: "slow",
              icon: "falExclamationTriangle"
            });

            setLocalChecklist(previousState);
            localStorage.setItem(`checklist_${checklist_id}`, JSON.stringify(previousState));
          }
        }
      );

      console.log("full response received:", updateResponse);
      
    } catch (error) {
      console.error("Error updating check:", error);
      showToast({
        title: "Update failed",
        text: "Failed to update check status. The event could not be sent to the server.",
        duration: "slow",
        icon: "falExclamationTriangle"
      });

      setLocalChecklist(previousState);
      localStorage.setItem(`checklist_${checklist_id}`, JSON.stringify(previousState));
    } 
  } 

  const prepareUpdateData = () => {
    const checks = localChecklist.sections.flatMap(section => 
      section.checks.map(check => ({
        id: check.id,
        status: check.status,
        comment: check.comment || ""
      }))
    );

    return {
      status: localChecklist.status,
      checks: checks
    }
  };

  const handleSaveChecklist = () => {
    if (isChecklistClosed) {
      showToast({
        title: "Checklist Closed",
        text: "This checklist is closed and cannot be modified",
        duration: "slow",
        icon: "falInfoCircle"
      });
      return;
    }

    try {
      localStorage.setItem(`checklist_${checklist_id}`, JSON.stringify(localChecklist));
      setFeedbackMessage({ type: 'success', text: 'Checklist saved successfully' });
      showToast({
        title: "Saved locally",
        text: "Checklist saved to local storage",
        duration: "slow",
        icon: "falSave"
      });
    } catch (error) {
      setFeedbackMessage({ type: 'error', text: 'Failed to save checklist' });
    }
  }

  const allRequiredChecksCompleted = () => {
    if (!template || !localChecklist.sections) return false;

    for (const section of localChecklist.sections) {
      for (const check of section.checks) {
        const checkTemplate = findCheckTemplateById(template, check.tag);

        if (!checkTemplate) continue;

        if (checkTemplate.approve?.required === true) {
          if (check.status === 0) {
            return false;
          }
        }
      }
    }
    return true;
  }

  const handleCloseChecklist = () => {

    if (isChecklistClosed) {
      navigate("/checklists");
      return;
    }
    if (!allRequiredChecksCompleted()) {
      showToast({
        title: "Cannot close checklist",
        text: "All required checks must be completed before closing the checklist",
        duration: "slow",
        icon: "falExclamationTriangle"
      });
      return;
    }

    const updateData = {
      ...prepareUpdateData(),
      status: ChecklistStatus.CLOSED
    };

    setIsSavingToServer(true);

    updateChecklist(
      {
        id: checklist_id,
        data: updateData
      },
      {
        onSuccess: (responseData) => {
          setIsSavingToServer(false);
          console.log("checklist close response:", responseData);

          if (responseData?.checklist) {
            setLocalChecklist(responseData.checklist);
            setIsChecklistClosed(responseData.checklist.status === ChecklistStatus.CLOSED);
          }

          localStorage.removeItem(`checklist_${checklist_id}`);
          setIsChecklistClosed(updateChecklist.status === ChecklistStatus.CLOSED);
          if (responseData?.checklistClosed) {
            showSuccess("Checklist has been successfully closed");
          }
          
          navigate("/checklists");
        },

        onError: (error) => {
          setIsSavingToServer(false);
          setFeedbackMessage({
            type: 'error',
            text: `Failed to close checklist: ${error.message}`
          });

          showToast({
            title: "Failed to Close Checklist",
            text: "An error occurred while closing the checklist. Please try again.",
            duration: "slow",
            icon: "falExclamationTriangle"
          });
        }
      }
    );
  }

  const handleClearStorage = () => {
    try {
      localStorage.clear();
      if (checklist) {
        setLocalChecklist(JSON.parse(JSON.stringify(checklist)));
      } else {
        setLocalChecklist({});
      }
      setFeedbackMessage({ type: 'success', text: 'Local storage cleared' });

      showToast({
        title: "Local Storage Cleared",
        duration: "slow",
        icon: 'falExclamationTriangle'
      });
    } catch (error) {
      console.error("Failed to clear local storage", error);
      setFeedbackMessage({ type: 'error', text: 'Failed to clear local storage' });
    }
  }

  const findCheckById = (checklist, checkId) => {
    if (!checklist || !checklist.sections) return null;
    for (const section of checklist.sections) {
      const check = section.checks.find(c => c.id === checkId);
      if (check) return check;
    }
    return null;
  }

  const isChecked = (checkId) => {
    const check = findCheckById(localChecklist, checkId);
    return check ? check.status !== CheckStatus.PENDING : false;
  }

  const getStatusText = (checkId) => {
    const check = findCheckById(localChecklist, checkId);
    if (!check || check.status === CheckStatus.PENDING) {
      return { text: 'Pending', color: 'gray.45' };
    }

    if (check.status === CheckStatus.APPROVED) {
      return { text: 'Approved', color: 'digiGreen.45' };
    } else if (check.status === CheckStatus.DISCARDED) {
      return { text: 'Discarded', color: 'energyRed.45' };
    }

  }
  
  const getAccordionItems = (check) => {
    const checkTemplate = findCheckTemplateById(template, check.tag);
    const enableDiscard = checkTemplate?.discard?.allowed === true;

    const isRequired = checkTemplate?.approve?.required === true;

    return [{
      id: check.id,
      title: check.label,
      collapsed: true,
      content: (
        <Box p={2} flexDirection="column" gap={2} w="100%">
          <P alignSelf="flex-start">{check.info}</P>
            <Box flexDirection="row" justifyContent="space-between" alignItems="center" w="100%">
              <P color={getStatusText(check.id)?.color} fontWeight="demi">
                {getStatusText(check.id)?.text }
                {getStatusText(check.id)?.text === 'Pending' && isRequired && <span style={{color: 'red'}}>*</span>}
              </P>

              <Box justifyContent="flex-end" gap={2}>
                  <>
                    <Button
                      icon="falCheck"
                      variant="solidGreen"
                      size="md"
                      onClick={() => handleOpenModal(check.id, "approve")}
                      disabled={isChecked(check.id)}
                    />                

                  {enableDiscard && (
                    <Button
                      icon="falTimes"
                      variant="solidRed"
                      size="md"
                      onClick={() => handleOpenModal(check.id, "discard")}
                      disabled={isChecked(check.id)}
                    />
                  )}
                  
                  </>
            </Box>  
          </Box>
        </Box>
      )
    }];
  }

  const renderFeedbackMessage = () => {
    if (!feedbackMessage) return null;

    return (
      <Box
        p={2}
        mb={2}
      >
        <T color={feedbackMessage.type === 'error' ? 'energyRed.45' : 'digiGreen.45'}>
          {feedbackMessage.text}
        </T>
      </Box>
    );
  }

  return (
    <Box p={4} flexDirection="column" maxW="100%" mx="auto" overflow="hidden" w="100%">

      <ChecklistHeader 
        name={localChecklist.name}
        tag={localChecklist.tag}
        dateCreated={localChecklist.dateCreated}
        dateClosed={localChecklist.dateClosed}
      />

      {renderFeedbackMessage()}

      {localChecklist.sections?.map(section => (
        <Box key={section.id} flexDirection="column" mb={6}>
          <H3 mb={1} >{section.tag}</H3>
          <Box flexDirection="column" gap={1}>
            {section.checks?.map((check) => (
              <Card key={check.id} p={0}>
                <Accordion 
                  items={getAccordionItems(check)} 
                  variant='dark'
                />
              </Card>
            ))}
          </Box>
        </Box>
      ))}

      <Box justifyContent="center" g={4}>
        <Button
          variant="secondaryDark"
          onClick={handleSaveChecklist}
        >
          Save checklist
        </Button>
        <Button
          variant="primaryDark"
          onClick={handleCloseChecklist}
          isLoading={isSavingToServer}  
          ml={2}
        >
          { isChecklistClosed ? "Back to checklists" : "Close checklist" }
        </Button>

      </Box>

      <CheckModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        action={modalAction}
        checkLabel={checklist.sections?.flatMap(section => section.checks).find(check => check.id === selectedCheckId)?.label}
        onConfirm={handleConfirm}
        checkId={selectedCheckId}
        inputType={modalInputConfig.type}
        inputOptions={modalInputConfig.options}
        inputLabel={modalInputConfig.label}
        isRequired={modalInputConfig.required}
      />
    </Box>
  );
};

export default ChecklistView;